import { DateTime } from 'luxon';
import {
  getGoogleTokensFromSupabase,
  getMsftTokensFromSupabase,
  saveGoogleTokensToSupabase,
} from './supabase';
import {
  parseDateString,
  formatDateString,
  isWeekend,
  getDayName,
  createDateRange,
  generateBusinessTimeSlots
} from './date-utils';
import {
  fetchProfessionalEmailCalendarWithResolution,
  mapProfessionalEmailEventsToUnifiedShape,
} from './professional-email-calendar-bridge';

// Configuration constants from Lambda code
export const PROVIDERS = {
  GOOGLE: {
    API_URL: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
    TABLE_NAME: "GOOGLE_TOKENS",
    USER_INFO_URL: "https://www.googleapis.com/oauth2/v3/userinfo",
    PARAM_PREFIX: "/google_oauth/"
  },
  MICROSOFT: {
    API_URL: "https://graph.microsoft.com/v1.0/me/calendarView",
    TABLE_NAME: "MSFT_TOKENS",
    PARAM_PREFIX: "/msft_oauth/"
  }
};

/**
 * Business wall-clock zone. `generateBusinessTimeSlots` (date-utils) emits every
 * slot in this zone, so provider event times that carry no UTC offset — notably
 * a Google all-day `{ date: "YYYY-MM-DD" }` — are interpreted here too.
 */
export const CALENDAR_DEFAULT_TIMEZONE = "America/Chicago";

/**
 * Published defaults for the booking surface.
 *
 * `SLOT_DURATION` and `BUFFER_MINUTES` ARE read, via `normalizeSlotConfig`,
 * which also accepts the camelCase spellings. Previously the generator read
 * `config.slotDuration`/`config.bufferMinutes` while this object published
 * `SLOT_DURATION`/`BUFFER_MINUTES`, so a caller that overrode the DOCUMENTED
 * keys was silently ignored and `filterAvailableSlots` hardcoded 15 regardless.
 *
 * `WORK_HOURS` and `MAX_SLOTS_PER_DAY` are still NOT enforced here, and that is
 * deliberate: the authority for work hours is `getBusinessHours(email)` in
 * `date-utils`, which carries per-person overrides (Miclain books 8-18 with a
 * 10-11 exclusion). Honouring a spread `DEFAULT_CONFIG.WORK_HOURS` would
 * silently flatten those back to 9-17 on the live booking page. Decide that
 * question before wiring them up.
 */
export const DEFAULT_CONFIG = {
  SLOT_DURATION: 30,
  MAX_SLOTS_PER_DAY: 9,
  WORK_HOURS: { start: 9, end: 17 },
  BUFFER_MINUTES: 15
};

/** One bookable window. `start`/`end` are absolute instants (ISO 8601, UTC). */
export interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
  isLunchSlot?: boolean;
}

interface AvailableDay {
  date: string;
  slots: TimeSlot[];
}

/** Provider-shaped time: Google `{dateTime|date, timeZone}` / Microsoft `{dateTime, timeZone}`. */
export type RawEventTime =
  | string
  | { dateTime?: string | null; date?: string | null; timeZone?: string | null }
  | null
  | undefined;

/** The minimum an event must expose for availability math. */
export interface BusyEventLike {
  start?: RawEventTime;
  end?: RawEventTime;
  /** Google: `"cancelled"` for a deleted instance of a recurring series. */
  status?: string | null;
  /** Microsoft Graph equivalent. */
  isCancelled?: boolean | null;
  [key: string]: unknown;
}

/** A provider event as this service passes it around (source email attached). */
export interface CalendarEventLike extends BusyEventLike {
  source?: string;
  sourceEmail?: string;
}

/** Absolute busy window, epoch milliseconds, half-open `[start, end)`. */
export interface BusyInterval {
  start: number;
  end: number;
}

/**
 * A calendar read plus whether it FAILED.
 *
 * `events: []` from a failed read is indistinguishable from a genuinely empty
 * calendar, and an empty calendar is a fully bookable one. That is a
 * double-booking source, not a degraded-service inconvenience, so the booking
 * path has to be able to tell the two apart.
 */
export interface CalendarEventsResult {
  events: CalendarEventLike[];
  /** True when the read gave up after an ERROR rather than legitimately finding nothing. */
  fetchFailed: boolean;
  /** True when the address simply has no calendar connected (not an error). */
  notConnected: boolean;
}

/** Legacy SCREAMING_SNAKE + camelCase config shapes callers may pass. */
export type SlotConfigInput = Partial<{
  slotDurationMinutes: number;
  slotDuration: number;
  SLOT_DURATION: number;
  bufferMinutes: number;
  BUFFER_MINUTES: number;
  timezone: string;
  TIMEZONE: string;
  /** A slot starting sooner than this from "now" is not offered. Default 0 (drops past slots). */
  minimumNoticeMinutes: number;
  MINIMUM_NOTICE_MINUTES: number;
  /** Opt OUT of failing closed when a calendar read errors. Default: fail closed. */
  allowPartialAvailabilityOnFetchError: boolean;
  /** Present on `DEFAULT_CONFIG` spreads; deliberately not read (see DEFAULT_CONFIG). */
  WORK_HOURS: { start?: number; end?: number };
  MAX_SLOTS_PER_DAY: number;
}>;

/** Fully-resolved settings. Every field required — no `any`. */
export interface SlotConfig {
  slotDurationMinutes: number;
  bufferMinutes: number;
  timezone: string;
  minimumNoticeMinutes: number;
  allowPartialAvailabilityOnFetchError: boolean;
}

const firstNumber = (...values: Array<number | undefined>): number | undefined =>
  values.find((v): v is number => typeof v === "number" && Number.isFinite(v));

/** Resolve any accepted config shape into a complete, typed `SlotConfig`. */
export function normalizeSlotConfig(input?: SlotConfigInput | null): SlotConfig {
  const src: SlotConfigInput = input ?? {};

  const slotDurationMinutes =
    firstNumber(src.slotDurationMinutes, src.slotDuration, src.SLOT_DURATION) ??
    DEFAULT_CONFIG.SLOT_DURATION;
  const bufferMinutes =
    firstNumber(src.bufferMinutes, src.BUFFER_MINUTES) ?? DEFAULT_CONFIG.BUFFER_MINUTES;

  return {
    slotDurationMinutes:
      slotDurationMinutes > 0 ? slotDurationMinutes : DEFAULT_CONFIG.SLOT_DURATION,
    bufferMinutes: bufferMinutes >= 0 ? bufferMinutes : DEFAULT_CONFIG.BUFFER_MINUTES,
    timezone: src.timezone ?? src.TIMEZONE ?? CALENDAR_DEFAULT_TIMEZONE,
    minimumNoticeMinutes:
      firstNumber(src.minimumNoticeMinutes, src.MINIMUM_NOTICE_MINUTES) ?? 0,
    allowPartialAvailabilityOnFetchError: src.allowPartialAvailabilityOnFetchError === true,
  };
}

/**
 * Thrown when availability could not be computed SAFELY — at least one calendar
 * read failed, so the time that looks free is unverified.
 */
export class CalendarAvailabilityError extends Error {
  readonly code = "CALENDAR_FETCH_FAILED";
  /**
   * Which calendars failed. Kept OFF the message on purpose: `createPostRoute`
   * in `route-utilities` returns `error.message` verbatim to the caller, and the
   * public booking endpoint is anonymous — putting staff addresses in the
   * message would hand them to any visitor who can trigger the failure. Read
   * this property for logging instead.
   */
  readonly failedEmails: string[];

  constructor(failedEmails: string[]) {
    super(
      `Calendar availability is temporarily unavailable for ${failedEmails.length} of the ` +
        `requested calendars. Please try again shortly.`
    );
    this.name = "CalendarAvailabilityError";
    this.failedEmails = failedEmails;
  }
}

// ---------------------------------------------------------------------------
// Holidays
// ---------------------------------------------------------------------------

/** `n`-th `weekday` (ISO 1=Mon…7=Sun) of `month` (1-12), as `{month, day}`. */
function nthWeekdayOfMonth(
  year: number,
  month: number,
  weekday: number,
  n: number
): { month: number; day: number } {
  const first = DateTime.fromObject({ year, month, day: 1 }, { zone: "utc" });
  const delta = (weekday - first.weekday + 7) % 7;
  const dt = first.plus({ days: delta + (n - 1) * 7 });
  return { month: dt.month, day: dt.day };
}

/** Last `weekday` (ISO 1=Mon…7=Sun) of `month` (1-12), as `{month, day}`. */
function lastWeekdayOfMonth(
  year: number,
  month: number,
  weekday: number
): { month: number; day: number } {
  const last = DateTime.fromObject({ year, month, day: 1 }, { zone: "utc" })
    .endOf("month")
    .startOf("day");
  const delta = (last.weekday - weekday + 7) % 7;
  const dt = last.minus({ days: delta });
  return { month: dt.month, day: dt.day };
}

/**
 * US federal holidays observed by WTC for `year`, as `MM-DD` keys.
 *
 * The previous arithmetic (`1 + (15 - firstOfMonth.getDay())` and friends) was
 * wrong for most years. In 2026 it produced Jan 12 for MLK Day (3rd Monday is
 * Jan 19), Sunday May 31 for Memorial Day (May 25), **Aug 31** for Labor Day
 * (Sep 7), and Oct 5 for Columbus Day (Oct 12) — two failures per holiday: the
 * real holiday stayed fully bookable, and an ordinary workday was closed.
 */
export function holidayKeysForYear(year: number): string[] {
  const key = (month: number, day: number): string =>
    `${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const fmt = (md: { month: number; day: number }): string => key(md.month, md.day);
  return [
    key(1, 1), // New Year's Day
    fmt(nthWeekdayOfMonth(year, 1, 1, 3)), // MLK Day - 3rd Monday of January
    fmt(nthWeekdayOfMonth(year, 2, 1, 3)), // Presidents' Day - 3rd Monday of February
    fmt(lastWeekdayOfMonth(year, 5, 1)), // Memorial Day - last Monday of May
    key(7, 4), // Independence Day
    fmt(nthWeekdayOfMonth(year, 9, 1, 1)), // Labor Day - 1st Monday of September
    fmt(nthWeekdayOfMonth(year, 10, 1, 2)), // Columbus Day - 2nd Monday of October
    key(11, 11), // Veterans Day
    fmt(nthWeekdayOfMonth(year, 11, 4, 4)), // Thanksgiving - 4th Thursday of November
    key(12, 25), // Christmas Day
  ];
}

export class CalendarService {
  static BUFFER_MINUTES = 15; // Buffer time between meetings

  static createLogger(context: string) {
    const timestamp = new Date().toISOString();
    const logPrefix = `[${timestamp}][${context}]`;

    return {
      info: (message: string, data: unknown = "") => {
        console.log(`${logPrefix} INFO: ${message}`, data ? JSON.stringify(data, null, 2) : "");
      },
      warn: (message: string, data: unknown = "") => {
        console.warn(`${logPrefix} WARN: ${message}`, data ? JSON.stringify(data, null, 2) : "");
      },
      error: (message: string, error: unknown) => {
        console.error(`${logPrefix} ERROR: ${message}`, error);
        console.error("Stack:", error instanceof Error ? error.stack : undefined);
      },
      debug: (message: string, data: unknown = "") => {
        console.debug(`${logPrefix} DEBUG: ${message}`, data ? JSON.stringify(data, null, 2) : "");
      },
    };
  }

  /**
   * Check if a date is a workday — not a weekend, and not an OBSERVED holiday.
   *
   * The calendar day is read from the Date's LOCAL components, which is the
   * frame `parseDateString` / `formatDateString` / `generateBusinessTimeSlots`
   * all use for day identity in this module (a day is constructed at local
   * midnight and read back locally, so the two agree). Do not reinterpret it in
   * a named zone here — that shifts the day key by one for half the clock.
   */
  static isWorkday(date: Date): boolean {
    return !isWeekend(date) && !this.isObservedHoliday(date);
  }

  /** True when this exact calendar day is a federal holiday. */
  static isHoliday(date: Date): boolean {
    if (Number.isNaN(date.getTime())) return false;
    const key = `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    return holidayKeysForYear(date.getFullYear()).includes(key);
  }

  /**
   * True when the business is CLOSED for a holiday on this calendar day — the
   * holiday itself, or the weekday a weekend holiday is OBSERVED on.
   *
   * `adjustForObservedHoliday` computed the observed day but nothing ever
   * called it, so a Saturday July 4 (2026 is one) closed the already-closed
   * Saturday and left the observed Friday July 3 fully bookable.
   */
  static isObservedHoliday(date: Date): boolean {
    if (Number.isNaN(date.getTime())) return false;
    if (this.isHoliday(date)) return true;

    const dayOfWeek = date.getDay();
    if (dayOfWeek === 5) {
      // Friday standing in for a Saturday holiday.
      const saturday = new Date(date.getTime());
      saturday.setDate(saturday.getDate() + 1);
      return this.isHoliday(saturday);
    }
    if (dayOfWeek === 1) {
      // Monday standing in for a Sunday holiday.
      const sunday = new Date(date.getTime());
      sunday.setDate(sunday.getDate() - 1);
      return this.isHoliday(sunday);
    }
    return false;
  }

  /**
   * The weekday a holiday is OBSERVED on: Saturday holidays move to the
   * preceding Friday, Sunday holidays to the following Monday. Returns `null`
   * when `date` is not itself a holiday (unchanged contract).
   */
  static adjustForObservedHoliday(date: Date): Date | null {
    if (!this.isHoliday(date)) return null;
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 6) {
      const friday = new Date(date.getTime());
      friday.setDate(date.getDate() - 1);
      return friday;
    }
    if (dayOfWeek === 0) {
      const monday = new Date(date.getTime());
      monday.setDate(date.getDate() + 1);
      return monday;
    }
    return date;
  }

  static generateWorkdayTimeSlots(
    date: Date,
    config: SlotConfigInput | null = DEFAULT_CONFIG,
    extendedHours: boolean = false,
    email?: string
  ): TimeSlot[] {
    const logger = this.createLogger("generateWorkdayTimeSlots");
    const resolved = normalizeSlotConfig(config);

    logger.info(`Generating slots for date: ${formatDateString(date)}, email: ${email}`);

    // Use the shared workday check function
    if (!this.isWorkday(date)) {
      logger.info(`Date is not a workday (day ${date.getDay()}) - no slots generated`);
      return [];
    }

    // Use the consolidated business hours logic from date-utils
    const slots = generateBusinessTimeSlots(
      date,
      email,
      extendedHours,
      resolved.slotDurationMinutes,
      resolved.bufferMinutes
    );

    logger.info(`Generated ${slots.length} slots for ${date.toDateString()}`);

    return slots;
  }

  /**
   * Back-compat wrapper: events only, failure indistinguishable from "free".
   * Prefer `getCalendarEventsWithStatus` on any path that decides availability.
   */
  static async getCalendarEvents(
    email: string,
    startDate: string,
    endDate: string
  ): Promise<CalendarEventLike[]> {
    return (await this.getCalendarEventsWithStatus(email, startDate, endDate)).events;
  }

  /**
   * Fetch a calendar AND report whether the read failed.
   *
   * Every provider path used to funnel into `return []` on error, so a Google
   * 500 / expired token / failed refresh produced exactly the same value as a
   * genuinely empty calendar — a fully bookable day. `fetchFailed` is what lets
   * the booking path refuse to offer unverified time.
   */
  static async getCalendarEventsWithStatus(
    email: string,
    startDate: string,
    endDate: string
  ): Promise<CalendarEventsResult> {
    const logger = this.createLogger("getCalendarEvents");
    const ok = (events: CalendarEventLike[]): CalendarEventsResult => ({
      events,
      fetchFailed: false,
      notConnected: false,
    });

    try {
      // Try Google Calendar first
      const googleTokens = await getGoogleTokensFromSupabase(email);

      if (googleTokens?.access_token) {
        logger.info(`Fetching Google Calendar events for ${email}`);

        try {
          const events = await this.getGoogleCalendarEvents(googleTokens.access_token, startDate, endDate, email);

          return ok(events);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : String(error);
          // If token is expired or API returns Bad Request, try to refresh it
          if (message.includes('expired') || message.includes('invalid') || message.includes('Bad Request')) {
            logger.info(`Attempting to refresh Google token for ${email} due to: ${message}`);
            
            if (googleTokens.refresh_token) {
              try {
                const refreshedTokens = await this.refreshGoogleToken(googleTokens.refresh_token);
                if (refreshedTokens) {
                  // Save the new tokens
                  await this.saveGoogleTokens(email, refreshedTokens.accessToken, refreshedTokens.refreshToken);
                  // Retry with new token
                  const retryEvents = await this.getGoogleCalendarEvents(refreshedTokens.accessToken, startDate, endDate, email);

                  return ok(retryEvents);
                }
              } catch (refreshError) {
                logger.error(`Failed to refresh Google token for ${email}:`, refreshError);
              }
            }
          }
          throw error;
        }
      }

      // Try Microsoft Calendar
      const msftTokens = await getMsftTokensFromSupabase(email);
      
      if (msftTokens?.access_token) {
        logger.info(`Fetching Microsoft Calendar events for ${email}`);
        
        const events = await this.getMicrosoftCalendarEvents(msftTokens.access_token, startDate, endDate, email);

        return ok(events);
      }

      // Fallback: try service account with domain-wide delegation (if available)
      // Run before WTC Email bridge so SA Google stays authoritative when configured (legacy order).
      if (typeof window === 'undefined') {
        try {
          const serviceAccountEmail = process.env.GOOGLE_SERVICE_ACCOUNT_EMAIL;
          const serviceAccountPrivateKey = process.env.GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY;
          
          if (serviceAccountEmail && serviceAccountPrivateKey) {
            try {
              // Import server-only utility using dynamic path to prevent webpack bundling
              // @ts-ignore - webpack may not resolve this in client builds
              const serverModule = './calendar-service-server';
              const { getServiceAccountToken } = await import(serverModule);
              const saToken = await getServiceAccountToken(email, serviceAccountEmail, serviceAccountPrivateKey);
              
              if (saToken) {
                logger.info(`Fetching Google Calendar via service account for ${email}`);
                
                const events = await this.getGoogleCalendarEvents(saToken, startDate, endDate, email);

                return ok(events);
              }
            } catch (importErr: unknown) {
              // Server utility might not be available in all environments
              const msg = importErr instanceof Error ? importErr.message : String(importErr);
              logger.debug(`Service account utility not available or import failed: ${msg}`);
            }
          }
        } catch (saErr: unknown) {
          logger.error(`Service account calendar fetch failed for ${email}:`, saErr);
        }
      }

      // WTC Email / next-email-client calendar (additive when SA / OAuth did not return)
      if (typeof window === 'undefined') {
        try {
          const startDateObj = new Date(startDate + 'T00:00:00.000Z');
          const endDateObj = new Date(endDate + 'T23:59:59.999Z');
          const fromUtc = startDateObj.toISOString();
          const toUtc = endDateObj.toISOString();

          const resolution = await fetchProfessionalEmailCalendarWithResolution({
            email,
            fromUtc,
            toUtc,
          });

          if (resolution.resolved) {
            logger.info(
              `WTC Email calendar for ${email}: ${resolution.events.length} events (next-email-client)`
            );
            return ok(mapProfessionalEmailEventsToUnifiedShape(resolution.events, email));
          }
        } catch (peErr: unknown) {
          const msg = peErr instanceof Error ? peErr.message : String(peErr);
          logger.debug(`WTC Email calendar bridge failed for ${email}: ${msg}`);
        }
      }

      // No provider is connected for this address. That is NOT a fetch failure:
      // the public-booking handler already partitions connected vs disconnected
      // attendees upstream before asking for availability.
      logger.info(`No calendar tokens found for ${email}`);

      return { events: [], fetchFailed: false, notConnected: true };
    } catch (error) {
      logger.error(`Error fetching calendar events for ${email}:`, error);

      return { events: [], fetchFailed: true, notConnected: false };
    }
  }

  static async getGoogleCalendarEvents(accessToken: string, startDate: string, endDate: string, userEmail?: string): Promise<CalendarEventLike[]> {
    const logger = this.createLogger("getGoogleCalendarEvents");
    const events: CalendarEventLike[] = [];
    let pageToken: string | null = null;

    logger.info(`Fetching Google Calendar events from ${startDate} to ${endDate}${userEmail ? ` for ${userEmail}` : ''}`);

    do {
      // Convert dates to ISO format for Google Calendar API
      // Parse dates as UTC to avoid timezone issues
      const startDateObj = new Date(startDate + 'T00:00:00.000Z');
      const endDateObj = new Date(endDate + 'T23:59:59.999Z');
      
      const timeMin = startDateObj.toISOString();
      const timeMax = endDateObj.toISOString();
      
      const params: URLSearchParams = new URLSearchParams({
        timeMin,
        timeMax,
        singleEvents: 'true',
        maxResults: '2500',
        ...(pageToken && { pageToken })
      });
      
      logger.info(`Google Calendar API request: timeMin=${timeMin}, timeMax=${timeMax}`);

      const response: Response = await fetch(`${PROVIDERS.GOOGLE.API_URL}?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Google Calendar API error: ${response.status} ${response.statusText} - ${errorText}`, null);
        
        // If it's a 401/403 error, the token might be expired
        if (response.status === 401 || response.status === 403) {
          logger.error(`Access token appears to be expired or invalid for ${userEmail}`, null);
          throw new Error(`Access token expired or invalid: ${response.statusText}`);
        }
        
        throw new Error(`Google Calendar API error: ${response.statusText}`);
      }

      const data = (await response.json()) as {
        items?: CalendarEventLike[];
        nextPageToken?: string | null;
      };
      const items = data.items ?? [];

      // Add source email to each event (same as unified calendar API)
      const eventsWithSource = items.map((event) => ({
        ...event,
        source: userEmail || 'unknown'
      }));

      events.push(...eventsWithSource);
      pageToken = data.nextPageToken ?? null;

      logger.info(`Fetched ${items.length} events, pageToken: ${pageToken ? 'present' : 'none'}`);
    } while (pageToken);

    logger.info(`Total events fetched: ${events.length}`);
    return events;
  }

  static async getMicrosoftCalendarEvents(accessToken: string, startDate: string, endDate: string, userEmail?: string): Promise<CalendarEventLike[]> {
    const logger = this.createLogger("getMicrosoftCalendarEvents");
    const events: CalendarEventLike[] = [];
    let nextLink: string | null = null;

    logger.info(`Fetching Microsoft Calendar events from ${startDate} to ${endDate}${userEmail ? ` for ${userEmail}` : ''}`);

    do {
      const params = new URLSearchParams({
        $select: 'id,subject,start,end,location,attendees,body,isAllDay,showAs,importance,organizer,creator',
        $orderby: 'start/dateTime',
        $top: '1000',
        startDateTime: startDate,
        endDateTime: endDate,
      });

      const url: string = nextLink || `${PROVIDERS.MICROSOFT.API_URL}?${params}`;
      const response: Response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        logger.error(`Microsoft Calendar API error: ${response.statusText}`, null);
        throw new Error(`Microsoft Calendar API error: ${response.statusText}`);
      }

      const data = (await response.json()) as {
        value?: CalendarEventLike[];
        '@odata.nextLink'?: string | null;
      };
      const value = data.value ?? [];

      // Add source email to each event (same as unified calendar API)
      const eventsWithSource = value.map((event) => ({
        ...event,
        source: userEmail || 'unknown'
      }));

      events.push(...eventsWithSource);
      nextLink = data['@odata.nextLink'] ?? null;

      logger.info(`Fetched ${value.length} events, nextLink: ${nextLink ? 'present' : 'none'}`);
    } while (nextLink);

    logger.info(`Total events fetched: ${events.length}`);
    return events;
  }

  /**
   * Read one provider time into an epoch-millisecond instant.
   *
   * Handles all three shapes that reach this module: a bare ISO string, Google /
   * Microsoft `{ dateTime, timeZone }`, and a Google all-day `{ date }`. The old
   * `new Date(event.start?.dateTime || event.start)` produced `Invalid Date` for
   * the all-day shape (`new Date(someObject)`), and every comparison against an
   * Invalid Date is false — so an all-day block removed no slots at all.
   */
  static readEventInstant(
    time: RawEventTime,
    timezone: string = CALENDAR_DEFAULT_TIMEZONE
  ): number | null {
    if (!time) return null;
    if (typeof time === "string") {
      const dt = DateTime.fromISO(time, { zone: timezone });
      return dt.isValid ? dt.toMillis() : null;
    }
    if (typeof time.dateTime === "string" && time.dateTime.length > 0) {
      const dt = DateTime.fromISO(time.dateTime, { zone: time.timeZone || timezone });
      return dt.isValid ? dt.toMillis() : null;
    }
    if (typeof time.date === "string" && time.date.length > 0) {
      // All-day: a bare `yyyy-MM-dd` is midnight in the CALENDAR's zone, not UTC.
      // Google's all-day `end.date` is already exclusive, so no +1 day here.
      const dt = DateTime.fromISO(time.date, { zone: time.timeZone || timezone });
      return dt.isValid ? dt.startOf("day").toMillis() : null;
    }
    return null;
  }

  /**
   * Absolute busy windows for provider events, dropping cancelled ones.
   *
   * A cancelled instance of a recurring series still comes back from Google with
   * `singleEvents=true` (`status: "cancelled"`); counting it blocked a slot the
   * person had actually freed up.
   */
  static toBusyIntervals(
    events: readonly BusyEventLike[] | null | undefined,
    timezone: string = CALENDAR_DEFAULT_TIMEZONE
  ): BusyInterval[] {
    const out: BusyInterval[] = [];
    for (const event of events ?? []) {
      if (!event) continue;
      if (typeof event.status === "string" && event.status.toLowerCase() === "cancelled") continue;
      if (event.isCancelled === true) continue;
      const start = this.readEventInstant(event.start, timezone);
      const end = this.readEventInstant(event.end, timezone);
      if (start === null || end === null || end <= start) continue;
      out.push({ start, end });
    }
    return out;
  }

  /** True when `slot` overlaps any busy interval, widened by `bufferMs` each side. */
  private static overlapsBusy(
    slot: TimeSlot,
    busy: readonly BusyInterval[],
    bufferMs: number
  ): boolean {
    const slotStart = Date.parse(slot.start);
    const slotEnd = Date.parse(slot.end);
    // An unparseable slot cannot be proven free, so treat it as blocked.
    if (Number.isNaN(slotStart) || Number.isNaN(slotEnd)) return true;
    return busy.some((b) => slotStart < b.end + bufferMs && slotEnd > b.start - bufferMs);
  }

  static async getAvailableCalendarDays(
    emails: string[],
    startDate: string,
    endDate: string,
    config: SlotConfigInput | null = DEFAULT_CONFIG,
    extendedHours: boolean = false,
    options: { now?: Date } = {}
  ): Promise<AvailableDay[]> {
    const logger = this.createLogger("getAvailableCalendarDays");
    const resolved = normalizeSlotConfig(config);
    const now = options.now ?? new Date();

    logger.info(`Getting available calendar days for ${emails.length} emails from ${startDate} to ${endDate}`);

    try {
      // Get calendar events for all emails
      const allEvents: CalendarEventLike[] = [];
      const failedEmails: string[] = [];
      for (const email of emails) {
        const result = await this.getCalendarEventsWithStatus(email, startDate, endDate);

        if (result.fetchFailed) failedEmails.push(email);

        allEvents.push(...result.events.map((event) => ({
          ...event,
          sourceEmail: email
        })));
      }

      // Fail CLOSED. A calendar we could not read is not a calendar we know is
      // free, and returning its whole workday as bookable is how the booking
      // page double-books. Callers who genuinely prefer partial availability
      // opt in with `allowPartialAvailabilityOnFetchError`.
      if (failedEmails.length > 0) {
        if (!resolved.allowPartialAvailabilityOnFetchError) {
          throw new CalendarAvailabilityError(failedEmails);
        }
        logger.warn(
          `Serving availability despite ${failedEmails.length} failed calendar read(s)`,
          { failedEmails }
        );
      }

      // Generate date range using safe date parsing
      const start = parseDateString(startDate);
      const end = parseDateString(endDate);
      const availableDays: AvailableDay[] = [];

      // Use the centralized date range utility
      const dateRange = createDateRange(start, end);

      for (const currentDate of dateRange) {
        logger.info(`Processing date: ${currentDate.toDateString()}, day of week: ${currentDate.getDay()} (${getDayName(currentDate)})`);

        // Process each email separately to handle different business hours
        for (const email of emails) {
          const daySlots = this.generateWorkdayTimeSlots(currentDate, config, extendedHours, email);

          if (daySlots.length > 0) {
            // Filter out slots that conflict with existing events for this specific email
            const emailEvents = allEvents.filter((event) => event.sourceEmail === email);

            let availableSlots = this.filterAvailableSlots(daySlots, emailEvents, currentDate, config);
            // Never offer a slot that has already started, or one inside the
            // minimum-notice window. Nothing filtered on `now` before, so at
            // 3pm the page still offered this morning's 9:00.
            availableSlots = this.filterSlotsByMinimumNotice(
              availableSlots,
              now,
              resolved.minimumNoticeMinutes
            );

            if (availableSlots.length > 0) {
              // Check if we already have this date in availableDays
              const existingDay = availableDays.find(day => day.date === formatDateString(currentDate));
              if (existingDay) {
                // Merge slots for this email into existing day
                existingDay.slots = [...existingDay.slots, ...availableSlots];
              } else {
                // Create new day entry
                availableDays.push({
                  date: formatDateString(currentDate),
                  slots: availableSlots
                });
              }
            }
          }
        }
      }

      // Deduplicate slots within each day (same start/end time from different people)
      const deduplicatedDays = availableDays.map(day => {
        const uniqueSlots: TimeSlot[] = [];
        const seenSlots = new Set<string>();

        for (const slot of day.slots) {
          // Create a unique key based on start and end time
          const slotKey = `${slot.start}-${slot.end}`;

          if (!seenSlots.has(slotKey)) {
            seenSlots.add(slotKey);
            uniqueSlots.push(slot);
          }
        }

        return {
          ...day,
          slots: uniqueSlots
        };
      }).filter(day => day.slots.length > 0); // Remove days with no slots after deduplication

      logger.info(`Found ${deduplicatedDays.length} available days with slots (after deduplication)`);

      return deduplicatedDays;

    } catch (error) {
      logger.error("Error getting available calendar days:", error);
      throw error;
    }
  }

  /** Drop slots starting sooner than `minimumNoticeMinutes` from `now` (past slots included). */
  static filterSlotsByMinimumNotice(
    slots: readonly TimeSlot[],
    now: Date,
    minimumNoticeMinutes: number = 0
  ): TimeSlot[] {
    const threshold = now.getTime() + Math.max(0, minimumNoticeMinutes) * 60_000;
    return slots.filter((slot) => {
      const start = Date.parse(slot.start);
      return !Number.isNaN(start) && start >= threshold;
    });
  }

  /**
   * Remove every slot overlapped by a busy interval (widened by the configured
   * buffer on each side).
   *
   * Busy events are now matched against the SLOT. The previous version first
   * narrowed `events` to those whose START fell inside the target calendar day,
   * so an overnight block (yesterday 22:00 → today 09:00), a multi-day block,
   * and an all-day block all failed to remove a single slot — the page offered
   * time the person was already busy.
   *
   * Comparison is half-open, so with `bufferMinutes: 0` a back-to-back meeting
   * that ends exactly when the slot starts does NOT block it.
   *
   * `_date` is retained for call-site compatibility and is no longer needed.
   */
  static filterAvailableSlots(
    slots: readonly TimeSlot[],
    events: readonly BusyEventLike[] | null | undefined,
    _date?: Date,
    config: SlotConfigInput | null = DEFAULT_CONFIG
  ): TimeSlot[] {
    const resolved = normalizeSlotConfig(config);
    const busy = this.toBusyIntervals(events, resolved.timezone);
    const bufferMs = resolved.bufferMinutes * 60_000;

    return slots.filter((slot) => !this.overlapsBusy(slot, busy, bufferMs));
  }

  /**
   * Refresh Google access token using refresh token
   */
  static async refreshGoogleToken(refreshToken: string): Promise<{ accessToken: string; refreshToken?: string } | null> {
    const logger = this.createLogger("refreshGoogleToken");
    
    try {
      const clientId = process.env.UNIFIEDTIME_GOOGLE_OAUTH_CLIENT_ID;
      const clientSecret = process.env.UNIFIEDTIME_GOOGLE_OAUTH_CLIENT_SECRET;
      
      if (!clientId || !clientSecret) {
        logger.error("Google OAuth credentials not configured", null);
        return null;
      }

      const response = await fetch("https://oauth2.googleapis.com/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          client_id: clientId,
          client_secret: clientSecret,
          grant_type: "refresh_token",
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Failed to refresh token: ${response.statusText} - ${errorText}`, null);
        return null;
      }

      const data = (await response.json()) as {
        access_token: string;
        refresh_token?: string | null;
      };
      logger.info("Successfully refreshed Google token");

      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token || refreshToken
      };
    } catch (error) {
      logger.error("Error refreshing Google token:", error);
      return null;
    }
  }

  /**
   * Save Google tokens to Supabase
   */
  static async saveGoogleTokens(email: string, accessToken: string, refreshToken?: string): Promise<boolean> {
    const logger = this.createLogger("saveGoogleTokens");
    
    try {
      const success = await saveGoogleTokensToSupabase(email, accessToken, refreshToken);
      if (success) {
        logger.info(`Successfully saved Google tokens for ${email}`);
      } else {
        logger.error(`Failed to save Google tokens for ${email}`, null);
      }
      return success;
    } catch (error) {
      logger.error(`Error saving Google tokens for ${email}:`, error);
      return false;
    }
  }
}
